import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR, formatDate, formatDateTime, todayISO } from "../lib/format";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Truck, Package, PackageCheck, Undo2, Clock } from "lucide-react";
import { generateDeliveryOrderPDF } from "../lib/deliveryOrderPdf";
import PdfPreviewDialog from "../components/PdfPreviewDialog";

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};

const DELIVERY_META = {
  belum: { label: "Belum Kirim", color: "bg-slate-100 text-slate-700 border-slate-200", icon: Clock },
  otw: { label: "OTW", color: "bg-blue-100 text-blue-800 border-blue-200", icon: Truck },
  terkirim: { label: "Terkirim", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: PackageCheck },
  retur: { label: "Retur", color: "bg-red-100 text-red-700 border-red-200", icon: Undo2 },
};

const DELIVERY_OPTIONS = [
  { key: "belum", label: "Belum Kirim (Reset)" },
  { key: "otw", label: "Sedang Dikirim (OTW)" },
  { key: "terkirim", label: "Sudah Terkirim" },
  { key: "retur", label: "Retur / Ditolak Customer" },
];

export default function Sales() {
  const [sos, setSos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [company, setCompany] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", customer_name: "", customer_po: "", order_date: todayISO(), notes: "", items: [], status: "draft", include_ppn: true, ppn_percent: 11 });

  // Delivery tracking dialog state
  const [trackSO, setTrackSO] = useState(null);
  const [trackForm, setTrackForm] = useState({ status: "otw", note: "", courier: "", tracking_number: "" });
  const [historySO, setHistorySO] = useState(null);
  const [pdfPrev, setPdfPrev] = useState({ open: false, url: "", filename: "", title: "" });

  const load = () => Promise.all([
    api.get("/sales-orders").then((r) => setSos(r.data)),
    api.get("/customers").then((r) => setCustomers(r.data)),
    api.get("/products").then((r) => setProducts(r.data)),
    api.get("/company-profile").then((r) => setCompany(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const addItem = () => {
    if (products.length === 0) return toast.error("Belum ada produk");
    const p = products[0];
    setForm({ ...form, items: [...form.items, { product_id: p.id, sku: p.sku, name: p.name, quantity: 1, price: p.sale_price }] });
  };
  const updateItem = (i, field, value) => {
    const items = [...form.items];
    if (field === "product_id") {
      const p = products.find((x) => x.id === value);
      if (p) items[i] = { ...items[i], product_id: p.id, sku: p.sku, name: p.name, price: p.sale_price };
    } else items[i] = { ...items[i], [field]: field === "quantity" || field === "price" ? Number(value) : value };
    setForm({ ...form, items });
  };
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, it) => s + it.quantity * it.price, 0);
  const ppn = form.include_ppn ? subtotal * (Number(form.ppn_percent) || 0) / 100 : 0;
  const total = subtotal + ppn;

  const save = async () => {
    if (!form.customer_id) return toast.error("Pilih customer");
    if (form.items.length === 0) return toast.error("Tambahkan minimal 1 item");
    try {
      await api.post("/sales-orders", form);
      toast.success(form.status === "approved" ? "SO disetujui, invoice otomatis terbit" : "SO disimpan");
      setOpen(false);
      setForm({ customer_id: "", customer_name: "", customer_po: "", order_date: todayISO(), notes: "", items: [], status: "draft", include_ppn: company.default_include_ppn ?? true, ppn_percent: company.default_ppn_percent ?? 11 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const approve = async (id) => {
    if (!window.confirm("Setujui SO ini? Invoice akan otomatis terbit dan stok berkurang.")) return;
    try { await api.post(`/sales-orders/${id}/approve`); toast.success("SO disetujui — Invoice terbit"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const printDO = (so) => {
    const custFromList = customers.find((c) => c.id === so.customer_id) || {};
    // Prioritaskan snapshot di SO, fallback ke master customer
    const cust = {
      name: so.customer_name || custFromList.name,
      address: so.customer_address || custFromList.address,
      phone: so.customer_phone || custFromList.phone,
      contact: so.customer_pic || custFromList.contact,
    };
    try {
      const res = generateDeliveryOrderPDF({ so, customer: cust, company, preview: true });
      if (res?.url) setPdfPrev({ open: true, url: res.url, filename: res.filename, title: `Preview DO ${so.do_number || so.so_number}` });
    } catch (e) { toast.error("Gagal cetak DO: " + e.message); }
  };

  const openTrack = (so) => {
    setTrackSO(so);
    setTrackForm({
      status: so.delivery_status === "belum" ? "otw" : so.delivery_status,
      note: "", courier: so.courier || "", tracking_number: so.tracking_number || "",
    });
  };

  const submitTrack = async () => {
    try {
      await api.post(`/sales-orders/${trackSO.id}/delivery-status`, trackForm);
      toast.success(`Status pengiriman: ${DELIVERY_META[trackForm.status].label}`);
      setTrackSO(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const renderDeliveryBadge = (so) => {
    const meta = DELIVERY_META[so.delivery_status || "belum"];
    const Icon = meta.icon;
    return <Badge className={`${meta.color} hover:${meta.color} inline-flex items-center gap-1`}><Icon size={11} />{meta.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Penjualan</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Sales Orders</h1>
          <p className="text-sm text-slate-500 mt-1">Status "Approved" auto menerbitkan Invoice + PPN sesuai tarif dipilih (11–15%). Kelola pengiriman via tombol Kirim/Status.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="so-new-btn"><Plus size={16} className="mr-2" />Buat SO Baru</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Sales Order Baru</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => { const c = customers.find((x) => x.id === v); setForm({ ...form, customer_id: v, customer_name: c?.name || "" }); }}>
                  <SelectTrigger data-testid="so-customer-select"><SelectValue placeholder="Pilih customer" /></SelectTrigger>
                  <SelectContent>{customers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tanggal</Label><Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Nomor PO Customer <span className="text-xs text-slate-400 font-normal">(opsional — dari customer, bisa di-edit kapan saja)</span></Label>
                <Input value={form.customer_po} onChange={(e) => setForm({ ...form, customer_po: e.target.value })} placeholder="Cth: PO/2026/00123 dari customer" data-testid="so-customer-po-input" />
              </div>
              <div className="col-span-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="approved">Approved (Terbitkan Invoice)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="col-span-2 flex items-center justify-between rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="flex-1">
                  <Label className="cursor-pointer">Kenakan PPN</Label>
                  <div className="text-xs text-slate-500">Tarif fleksibel 11–15% sesuai kebijakan pajak</div>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={String(form.ppn_percent)} onValueChange={(v) => setForm({ ...form, ppn_percent: Number(v) })} disabled={!form.include_ppn}>
                    <SelectTrigger className="w-24 h-9" data-testid="so-ppn-rate"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="11">11%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="13">13%</SelectItem>
                      <SelectItem value="14">14%</SelectItem>
                      <SelectItem value="15">15%</SelectItem>
                    </SelectContent>
                  </Select>
                  <Switch checked={form.include_ppn} onCheckedChange={(v) => setForm({ ...form, include_ppn: v })} data-testid="so-ppn-toggle" />
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between mb-2"><Label>Item Barang</Label><Button size="sm" variant="outline" onClick={addItem} data-testid="so-add-item"><Plus size={14} className="mr-1" />Tambah Item</Button></div>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-2 rounded-lg">
                    <div className="col-span-5">
                      <Select value={it.product_id} onValueChange={(v) => updateItem(i, "product_id", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name} (stok: {p.stock})</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2"><Input type="number" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} /></div>
                    <div className="col-span-4"><Input type="number" placeholder="Harga" value={it.price} onChange={(e) => updateItem(i, "price", e.target.value)} /></div>
                    <div className="col-span-1"><Button size="sm" variant="ghost" onClick={() => removeItem(i)}><Trash2 size={14} className="text-red-500" /></Button></div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <div className="text-sm space-y-1 w-64">
                  <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="font-mono">{formatIDR(subtotal)}</span></div>
                  {form.include_ppn && <div className="flex justify-between text-slate-600"><span>PPN {form.ppn_percent}%</span><span className="font-mono">{formatIDR(ppn)}</span></div>}
                  <div className="flex justify-between font-display font-bold text-navy-900 pt-1 border-t"><span>Total</span><span className="font-mono">{formatIDR(total)}</span></div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="so-save-btn">Simpan SO</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="card-elevated"><CardContent className="p-4">
        <div className="overflow-x-auto">
          <Table data-testid="so-table">
            <TableHeader>
              <TableRow>
                <TableHead>No. SO</TableHead>
                <TableHead>Nomor PO Customer</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Delivery Order</TableHead>
                <TableHead>Pengiriman</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sos.map((so) => (
                <TableRow key={so.id} data-testid={`so-row-${so.so_number}`}>
                  <TableCell className="font-mono text-xs font-semibold">{so.so_number}</TableCell>
                  <TableCell>
                    <input
                      type="text"
                      className="w-32 px-2 py-1 border border-slate-200 rounded text-xs font-mono focus:border-navy-900 focus:outline-none focus:ring-1 focus:ring-navy-900 bg-white"
                      placeholder="No. PO Customer"
                      defaultValue={so.customer_po || ""}
                      onBlur={async (e) => {
                        const v = e.target.value.trim();
                        if (v === (so.customer_po || "")) return;
                        try { await api.put(`/sales-orders/${so.id}/customer-po`, { customer_po: v }); toast.success("Nomor PO diperbarui"); load(); }
                        catch (err) { toast.error(err?.response?.data?.detail || "Gagal update"); }
                      }}
                      data-testid={`so-customer-po-${so.so_number}`}
                    />
                  </TableCell>
                  <TableCell>{so.customer_name}</TableCell>
                  <TableCell>{formatDate(so.order_date)}</TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(so.total)}</TableCell>
                  <TableCell><Badge className={STATUS_STYLE[so.status]}>{so.status.toUpperCase()}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{so.invoice_number || "-"}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => printDO(so)} data-testid={`so-do-${so.so_number}`}>
                      <Truck size={14} className="mr-1" />Cetak DO
                    </Button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {renderDeliveryBadge(so)}
                      {so.delivery_history?.length > 0 && (
                        <button className="text-[10px] text-slate-500 hover:text-navy-900 underline text-left" onClick={() => setHistorySO(so)} data-testid={`so-history-${so.so_number}`}>Lihat riwayat ({so.delivery_history.length})</button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {so.status === "draft" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve(so.id)} data-testid={`so-approve-${so.so_number}`}><CheckCircle2 size={14} className="mr-1" />Setujui</Button>}
                    <Button size="sm" variant="outline" className="ml-2" onClick={() => openTrack(so)} data-testid={`so-track-${so.so_number}`}><Package size={14} className="mr-1" />Kirim</Button>
                  </TableCell>
                </TableRow>
              ))}
              {sos.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">Belum ada SO</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      {/* Tracking Dialog */}
      <Dialog open={!!trackSO} onOpenChange={(v) => !v && setTrackSO(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Update Status Pengiriman</DialogTitle></DialogHeader>
          {trackSO && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div className="font-mono text-xs text-slate-500">{trackSO.so_number}</div>
                <div className="font-semibold">{trackSO.customer_name}</div>
                <div className="text-slate-500 mt-1">Status saat ini: {renderDeliveryBadge(trackSO)}</div>
              </div>
              <div>
                <Label>Status Baru</Label>
                <Select value={trackForm.status} onValueChange={(v) => setTrackForm({ ...trackForm, status: v })}>
                  <SelectTrigger data-testid="track-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DELIVERY_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(trackForm.status === "otw" || trackForm.status === "terkirim") && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Kurir / Ekspedisi</Label><Input placeholder="JNE / SiCepat / Internal" value={trackForm.courier} onChange={(e) => setTrackForm({ ...trackForm, courier: e.target.value })} data-testid="track-courier-input" /></div>
                  <div><Label>No. Resi</Label><Input placeholder="123456789" value={trackForm.tracking_number} onChange={(e) => setTrackForm({ ...trackForm, tracking_number: e.target.value })} data-testid="track-tracking-input" /></div>
                </div>
              )}
              <div>
                <Label>Catatan</Label>
                <Textarea placeholder={trackForm.status === "retur" ? "Alasan retur..." : "Catatan opsional..."} value={trackForm.note} onChange={(e) => setTrackForm({ ...trackForm, note: e.target.value })} data-testid="track-note-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrackSO(null)}>Batal</Button>
            <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={submitTrack} data-testid="track-submit-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Timeline Dialog */}
      <Dialog open={!!historySO} onOpenChange={(v) => !v && setHistorySO(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Riwayat Pengiriman</DialogTitle></DialogHeader>
          {historySO && (
            <div>
              <div className="bg-slate-50 rounded-lg p-3 text-sm mb-4">
                <div className="font-mono text-xs text-slate-500">{historySO.so_number} · {historySO.customer_name}</div>
                {historySO.courier && <div className="text-slate-600 mt-1">Kurir: <strong>{historySO.courier}</strong>{historySO.tracking_number && ` · Resi: ${historySO.tracking_number}`}</div>}
              </div>
              <div className="relative pl-6 space-y-4">
                <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-slate-200"></div>
                {[...(historySO.delivery_history || [])].reverse().map((h, i) => {
                  const meta = DELIVERY_META[h.status];
                  const Icon = meta.icon;
                  return (
                    <div key={i} className="relative" data-testid={`history-entry-${i}`}>
                      <div className={`absolute -left-6 top-0 h-4 w-4 rounded-full flex items-center justify-center ${meta.color.replace("bg-", "bg-").replace("text-", "text-")}`}>
                        <Icon size={10} />
                      </div>
                      <div className="ml-2">
                        <div className="flex justify-between">
                          <Badge className={meta.color}>{meta.label}</Badge>
                          <span className="text-[11px] text-slate-500">{formatDateTime(h.timestamp)}</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">oleh <span className="font-mono">{h.updated_by}</span></div>
                        {h.note && <div className="text-sm text-slate-700 mt-1 italic">"{h.note}"</div>}
                      </div>
                    </div>
                  );
                })}
                {(!historySO.delivery_history || historySO.delivery_history.length === 0) && <div className="text-center text-slate-400 py-4">Belum ada riwayat</div>}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setHistorySO(null)}>Tutup</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <PdfPreviewDialog open={pdfPrev.open} url={pdfPrev.url} filename={pdfPrev.filename} title={pdfPrev.title} onClose={() => setPdfPrev({ open: false, url: "", filename: "", title: "" })} />
    </div>
  );
}
